/**
 * Demo "book a call" lead-capture endpoint (July 2026).
 *
 * BE source of truth: ``ando-agent/src/api/v1/endpoints/demo.py``
 *   - POST /demo/book-call-lead
 *
 * Called by ``DemoBookCallModal`` in ``apps/client`` when an ephemeral demo
 * visitor submits the "prenota 15 min" form. The BE forwards the lead to
 * Instantly + the sales Slack channel and (when known) returns a Calendly
 * URL so the FE can offer an immediate deep-link to book.
 *
 * The call is issued from the demo audience — no persistent user identity —
 * so the endpoint is public + rate-limited on the BE side.
 */
import { apiClient } from "../ApiClient.js";

export interface BookCallLeadSessionMetadata {
  readonly uptime_min: number;
  readonly queries_done: number;
  readonly first_wow_moment: string | null;
  readonly features_touched: readonly string[];
  readonly language: string;
  readonly source_utm: string | null;
}

export interface BookCallLeadInput {
  readonly email: string;
  readonly full_name: string;
  readonly company_name: string;
  readonly phone?: string;
  readonly message?: string;
  readonly session_metadata: BookCallLeadSessionMetadata;
}

/** Response status — ``"captured"`` first time we see the email, ``"duplicate"`` afterwards. */
export type BookCallLeadStatus = "captured" | "duplicate";

export interface BookCallLeadResponse {
  readonly status: BookCallLeadStatus;
  /** Optional booking deep-link — the FE opens it in a new tab when present. */
  readonly calendly_url?: string | null;
}

export function submitDemoBookCallLead(
  input: BookCallLeadInput,
): Promise<BookCallLeadResponse> {
  return apiClient.post<BookCallLeadResponse>("/demo/book-call-lead", input);
}
